'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useSiteConfig } from '@/contexts/SiteConfigContext';
import { defaultHomepageConfig, HomepageConfig } from '@/lib/homepage-config';

type FooterSectionConfig = HomepageConfig['footerSection'];
function SocialIcon({ icon }: { icon: string }) {
  const normalized = icon.toLowerCase();

  if (normalized === 'facebook' || normalized === 'fb') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" className="h-4 w-4 fill-current">
        <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z" />
      </svg>
    );
  }

  if (normalized === 'youtube' || normalized === 'yt') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" className="h-4 w-4 fill-current">
        <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.007 2.007 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.007 2.007 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31.4 31.4 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.007 2.007 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A99.788 99.788 0 0 1 7.858 2h.193zM6.4 5.209v4.818l4.157-2.408L6.4 5.209z" />
      </svg>
    );
  }

  if (normalized === 'tiktok' || normalized === 'tt') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        className="h-3.5 w-3.5 fill-current"
      >
        <path d="M9 0h1.98c.144.715.54 1.617 1.235 2.512C12.895 3.389 13.797 4 15 4v2c-1.753 0-3.07-.814-4-1.829V11a5 5 0 1 1-5-5v2a3 3 0 1 0 3 3V0z" />
      </svg>
    );
  }

  if (normalized === 'zalo') {
    return <span className="text-[10px] font-bold">Zalo</span>;
  }

  return <span className="text-[10px] font-bold">{icon.slice(0, 2).toUpperCase()}</span>;
}

function socialColor(icon: string) {
  const normalized = icon.toLowerCase();
  if (normalized === 'facebook' || normalized === 'fb') return 'bg-[#1877F2]';
  if (normalized === 'youtube' || normalized === 'yt') return 'bg-[#FF0000]';
  if (normalized === 'tiktok' || normalized === 'tt') return 'bg-[#111827]';
  if (normalized === 'zalo') return 'bg-[#0180C7]';
  return 'bg-[#374151]';
}

export default function Footer({ footerSection }: { footerSection?: FooterSectionConfig }) {
  const siteConfig = useSiteConfig();
  const initialFooter = siteConfig?.footer;
  const [remoteFooterSection, setRemoteFooterSection] = useState<FooterSectionConfig | null>(
    initialFooter?.footerSection || null
  );
  const [footerVisible, setFooterVisible] = useState(initialFooter?.footerVisible ?? true);
  const [quickSupportEnabled, setQuickSupportEnabled] = useState(true);

  const config = footerSection || remoteFooterSection || defaultHomepageConfig.footerSection;

  useEffect(() => {
    if (initialFooter) {
      setRemoteFooterSection(initialFooter.footerSection || null);
      setFooterVisible(initialFooter.footerVisible ?? true);
      return;
    }

    if (footerSection) return;

    fetch('/api/homepage-config')
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (payload?.footerSection) {
          setRemoteFooterSection(payload.footerSection);
        }
        if (typeof payload?.footerVisible === 'boolean') {
          setFooterVisible(payload.footerVisible);
        }
      })
      .catch(() => null);
  }, [footerSection, initialFooter]);

  useEffect(() => {
    fetch('/api/settings/public', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        setQuickSupportEnabled(payload?.settings?.support_config?.quickSupportEnabled !== false);
      })
      .catch(() => setQuickSupportEnabled(true));
  }, []);

  const normalize = (value: string) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .trim();

  const mergedGroups = useMemo(() => {
    const isBlockedFooterLink = (label: string) => normalize(label).includes('lap dat phong net');
    const isQuickSupportLink = (label?: string, href?: string) =>
      normalize(label || '') === 'ho tro nhanh' || String(href || '').trim() === '#chat-quick';

    const base = (config.groups || []).map((group) => ({
      title: group.title,
      links: [...(group.links || [])].filter(
        (link) =>
          !isBlockedFooterLink(link.label || '') &&
          (quickSupportEnabled || !isQuickSupportLink(link.label, link.href))
      ),
    }));

    return base;
  }, [config.groups, quickSupportEnabled]);

  if (!footerSection && !footerVisible) {
    return null;
  }

  const visibleGroups = mergedGroups.filter(
    (group) =>
      normalize(group.title) !== 'chinh sach & ho tro khach hang' &&
      normalize(group.title) !== 'ho tro nhanh' &&
      normalize(group.title) !== 'trang noi dung'
  );
  const contactLines = String(config.bottomText || '')
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 4);
  const supportQuickGroup = mergedGroups.find((group) => normalize(group.title) === 'ho tro nhanh');
  const supportQuickLinks =
    (supportQuickGroup?.links || []).length > 0
      ? (supportQuickGroup?.links || []).slice(0, 6)
      : mergedGroups
          .filter((group) => {
            const key = normalize(group.title);
            return key.includes('ho tro') || key.includes('chinh sach');
          })
          .flatMap((group) => group.links || [])
          .slice(0, 6);
  const mergedSupportQuickLinks = [...supportQuickLinks].filter(
    (link, index, array) =>
      array.findIndex(
        (candidate) =>
          String(candidate.href || '').trim() === String(link.href || '').trim() ||
          normalize(candidate.label || '') === normalize(link.label || '')
      ) === index
  );
  const isQuickSupportLink = (label?: string, href?: string) =>
    normalize(label || '') === 'ho tro nhanh' || String(href || '').trim() === '#chat-quick';
  const handleFooterLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    label?: string,
    href?: string
  ) => {
    if (!isQuickSupportLink(label, href)) return;
    event.preventDefault();
    window.dispatchEvent(new CustomEvent('dntech:open-chat'));
  };

  return (
    <footer className="relative mt-auto overflow-hidden border-t border-slate-700/80 bg-[#0b1220] text-slate-100">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_-20%,rgba(245,158,11,0.16),transparent_35%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_8%,rgba(251,191,36,0.12),transparent_34%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-60" />

      <div className="relative mx-auto w-full max-w-[1500px] px-4 py-5">
        <div className="grid gap-4 border-b border-slate-700/70 pb-4">
          <section className="rounded-2xl border border-slate-800/80 bg-slate-900/30 p-3.5">
            {(config.socials || []).length ? (
              <div className="mb-3 flex justify-end">
                <div className="flex flex-wrap gap-2">
                  {config.socials.map((social, socialIndex) => (
                    <Link
                      key={`${social.label}-${socialIndex}`}
                      href={social.href || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-white shadow-[0_8px_16px_rgba(0,0,0,0.25)] transition-transform duration-200 hover:-translate-y-0.5 ${socialColor(social.icon)}`}
                      aria-label={social.label}
                      title={social.label}
                    >
                      <SocialIcon icon={social.icon} />
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mb-3 grid gap-2.5 lg:grid-cols-3">
              <div className="rounded-xl border border-slate-700 bg-slate-800/65 p-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-300">
                  Thông Tin Cửa Hàng
                </p>
                <ul className="mt-2 space-y-1.5">
                  {contactLines.length ? (
                    contactLines.map((line, index) => (
                      <li key={`contact-${index}`} className="text-xs leading-5 text-slate-200">
                        {line}
                      </li>
                    ))
                  ) : (
                    <li className="text-xs leading-5 text-slate-300">Thông tin đang cập nhật.</li>
                  )}
                </ul>
              </div>

              <div className="rounded-xl border border-slate-700 bg-slate-800/65 p-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-300">
                  Hỗ Trợ Nhanh
                </p>
                <ul className="mt-2 space-y-1.5">
                  {mergedSupportQuickLinks.length ? (
                    mergedSupportQuickLinks.slice(0, 4).map((link, index) => (
                      <li key={`support-${link.label}-${index}`}>
                        <Link
                          href={link.href || '#'}
                          onClick={(event) =>
                            handleFooterLinkClick(event, link.label, link.href || '#')
                          }
                          className="group inline-flex items-start gap-2 text-xs leading-5 text-slate-200 transition-colors hover:text-amber-300"
                        >
                          <span className="mt-[2px] text-[10px] text-amber-400">▸</span>
                          <span className="line-clamp-1">{link.label}</span>
                        </Link>
                      </li>
                    ))
                  ) : (
                    <li className="text-xs leading-5 text-slate-300">Liên kết đang cập nhật.</li>
                  )}
                </ul>
              </div>

              <div className="rounded-xl border border-amber-300/20 bg-gradient-to-br from-amber-300/15 to-transparent p-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-300">
                  Dịch Vụ
                </p>
                <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
                  <span className="rounded-md border border-slate-700 bg-slate-900/70 px-2 py-1 text-slate-200">
                    Build PC
                  </span>
                  <span className="rounded-md border border-slate-700 bg-slate-900/70 px-2 py-1 text-slate-200">
                    Bảo hành
                  </span>
                  <span className="rounded-md border border-slate-700 bg-slate-900/70 px-2 py-1 text-slate-200">
                    Vệ sinh máy
                  </span>
                  <span className="rounded-md border border-slate-700 bg-slate-900/70 px-2 py-1 text-slate-200">
                    Kỹ thuật
                  </span>
                </div>
                <Link
                  href="/lien-he"
                  className="mt-2 inline-flex text-xs font-semibold text-amber-300 transition-colors hover:text-amber-200"
                >
                  Liên hệ hỗ trợ ngay
                </Link>
              </div>
            </div>

            {visibleGroups.length ? (
              <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
                {visibleGroups.map((section, index) => (
                  <div
                    key={`${section.title}-${index}`}
                    className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 shadow-[0_6px_18px_rgba(2,6,23,0.2)]"
                  >
                    <h4 className="pb-1.5 text-[12px] font-extrabold uppercase tracking-[0.08em] leading-5 text-slate-100 break-words">
                      {section.title}
                    </h4>

                    <ul className="space-y-1.5 border-t border-slate-800 pt-2">
                      {(section.links || []).slice(0, 8).map((link, linkIndex) => (
                        <li key={`${link.label}-${linkIndex}`}>
                          <Link
                            href={link.href || '#'}
                            onClick={(event) =>
                              handleFooterLinkClick(event, link.label, link.href || '#')
                            }
                            className="group inline-flex min-w-0 items-start gap-2 text-[13px] leading-5 text-slate-300 transition-colors hover:text-amber-300"
                          >
                            <span className="mt-[2px] text-[11px] text-amber-400 transition-transform group-hover:translate-x-0.5">
                              ▸
                            </span>
                            <span className="break-words">{link.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        </div>

        <div className="flex flex-col items-center justify-between gap-2 pt-3 text-xs text-slate-400 sm:flex-row">
          <p className="text-center sm:text-left">{config.bottomText}</p>
          <p className="text-center">© {new Date().getFullYear()} DN TECH. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
